<script setup>
import { useFilterSidePanel } from '@dashhudson/dashing-ui/filter-side-panel';
import { VisionAiAnimation } from '@dashhudson/dashing-ui/vision-ai-animation';
import { computed, onMounted, onUnmounted, ref, useTemplateRef, watch } from 'vue';

import { useVisionModelUpgrade } from '@/components/VisionAi/composables/useVisionModelUpgrade';
import { useHubspotStore } from '@/stores/hubspot';
import { useVisionAiStore } from '@/stores/vision-ai';

const visionAiStore = useVisionAiStore();
const emit = defineEmits(['openChatWindow']);

const props = defineProps({
  active: {
    type: Boolean,
    default: false,
  },
});

const animateVisionAiAnimation = ref(false);
const expand = ref(false);
const statusMessage = ref(null);

const {
  shouldShowUpgradeNotification,
  shouldShowUpgradeMessage,
  currentStatusMessage,
  markUpgradeDetected,
  trackVisionModelUpdated,
} = useVisionModelUpgrade();

let notificationTimeout = null;

function startVisionNotification() {
  animateVisionAiAnimation.value = true;
  statusMessage.value = currentStatusMessage.value;
  markUpgradeDetected();
}

function handleGlobalClick() {
  if (!shouldShowUpgradeNotification()) {
    return;
  }

  if (notificationTimeout) {
    clearTimeout(notificationTimeout);
  }

  notificationTimeout = setTimeout(() => {
    startVisionNotification();
    notificationTimeout = null;
  }, 3000);
}

onMounted(() => {
  document.addEventListener('click', handleGlobalClick);

  if (shouldShowUpgradeMessage()) {
    startVisionNotification();
  }
});

onUnmounted(() => {
  document.removeEventListener('click', handleGlobalClick);
  if (notificationTimeout) {
    clearTimeout(notificationTimeout);
  }
});

const { visible: filterVisible } = useFilterSidePanel();
const hubspotStore = useHubspotStore();

const globalRight = computed(() => {
  if (filterVisible.value) {
    if (hubspotStore.ready) {
      if (hubspotStore.isGdsVersion) {
        return '!right-[24.75rem]';
      }
      return '!right-[26.50rem]';
    }
    return '!right-[22rem]';
  }

  if (hubspotStore.ready) {
    if (hubspotStore.isGdsVersion) {
      return '!right-[2.75rem]';
    }
    return '!right-[4.50rem]';
  }
  return '!right-0';
});

const buttonPositionClasses = computed(() => `${globalRight.value} !fixed !bottom-4 !mr-4`);

function clearVisionNotification() {
  expand.value = false;
  animateVisionAiAnimation.value = false;
  statusMessage.value = null;
  trackVisionModelUpdated('notification_cleared');
}

async function handleButtonClicked(event) {
  visionAiStore.openChatWindow();
  clearVisionNotification();
  emit('openChatWindow', event);
}

const expanded = computed(() => expand.value || props.active);
const visionAiAnimation = useTemplateRef('visionAiAnimationRef');

watch(
  () => expanded.value,
  (newVal) => {
    if (newVal && visionAiAnimation.value) {
      visionAiAnimation.value.resetAnimation();
    }
  },
);

function handleMouseEnter() {
  if (statusMessage.value) {
    expand.value = true;
  }
}
</script>

<template>
  <div
    data-test="vision-ai-button-global"
    :class="['vision-ai-button !pointer-events-auto relative', buttonPositionClasses]"
    :style="{ zIndex: '9999' }"
  >
    <div
      :class="[
        'dash-bg-vision-ai',
        'dash-rounded-full',
        'py-2',
        'pl-2',
        'cursor-pointer',
        'overflow-hidden',
        'dash-transition-lg',
        { '!max-w-10 pr-2': !expanded, '!max-w-[350px] pr-4': expanded },
      ]"
      @mouseenter="handleMouseEnter"
      @click="handleButtonClicked($event)"
    >
      <div :class="['flex overflow-hidden', { 'gap-2': expanded, 'justify-center': !expanded }]">
        <VisionAiAnimation
          ref="visionAiAnimationRef"
          exponential-delay
          :disabled="!animateVisionAiAnimation"
        />
        <Transition name="fade">
          <div v-if="expanded" class="text-nowrap">
            <span class="typo-base-semibold dash-text-knockout">{{ statusMessage }}</span>
          </div>
        </Transition>
      </div>
    </div>
  </div>
</template>
